<script setup lang="ts">
import type {
	PropertyPhotoCarouselMobileEmits,
	PropertyPhotoCarouselMobileProps
} from '.';
import type { PropertyImage } from '~/core/entities/property';

import { breakpointsTailwind, useBreakpoints } from '@vueuse/core';
import { EventBusKeys } from '~/core/helpers/symbols';
import { getPropertyImageAlt } from '~/utils';

import { GlassButton } from '~/components/GlassButton';
import { HlsVideoPlayer } from '~/components/HlsVideoPlayer';
import { StandardSlider } from '~/components/StandardSlider';
import { Skeleton } from '~/components/ui/skeleton';
import VideoProgressBar from '~/components/VideoCard/VideoProgressBar.vue';

import IconMaterialSymbolsLightPlayArrow from '~icons/material-symbols-light/play-arrow';
import IconMaterialSymbolsLightVolumeOff from '~icons/material-symbols-light/volume-off';
import IconMaterialSymbolsLightVolumeUp from '~icons/material-symbols-light/volume-up';

interface CarouselItem {
	type: 'image' | 'video';
	image?: PropertyImage;
	videoUrl?: string;
	posterUrl?: string;
	index?: number;
}

const props = withDefaults(defineProps<PropertyPhotoCarouselMobileProps>(), {
	isLoading: false,
	sectionName: 'hero section'
});

const emit = defineEmits<PropertyPhotoCarouselMobileEmits>();

const standardSliderRef = useTemplateRef<{
	currentCarouselItemIndex: number;
	scrollableSlidesNumber: number;
	scrollTo: (index: number, immediate?: boolean) => void;
	reinitializeCarousel: (() => void) | undefined;
}>('standardSliderRef');
const mobileVideoPlayerRef = useTemplateRef<{
	playing: boolean;
	hasStartedPlaying: boolean;
	progress: number;
	isMuted: boolean;
	hasError: boolean;
	pause: () => void;
	toggleMute: () => void;
	togglePlay: () => Promise<void>;
	seekTo: (ratio: number) => void;
}>('mobileVideoPlayerRef');
const isMobileViewport = useBreakpoints(breakpointsTailwind).smaller('md');

const carouselItems = computed<CarouselItem[]>(() => {
	const items: CarouselItem[] = [];

	// If has video, show it as first item
	if (props.videoUrl) {
		items.push({
			type: 'video',
			videoUrl: props.videoUrl,
			posterUrl: props.posterUrl
		});
	}

	// Add all images
	props.images.forEach((image, index) => {
		items.push({
			type: 'image',
			image,
			index
		});
	});

	return items;
});
const visibleSlidesCount = computed(() =>
	Math.max(
		1,
		carouselItems.value.length -
			(standardSliderRef.value?.scrollableSlidesNumber ??
				carouselItems.value.length) +
			1
	)
);

const currentPhotoCount = computed(() => {
	const currentIndex = standardSliderRef.value?.currentCarouselItemIndex ?? 0;
	const hasVideo = !!props.videoUrl;

	// If on video slide, return 0
	if (hasVideo && currentIndex === 0) {
		return 0;
	}

	// Adjust index if video is present (first item is video, not photo)
	const photoIndex = hasVideo ? currentIndex - 1 : currentIndex;

	return Math.min(props.images.length, photoIndex + visibleSlidesCount.value);
});
const isVideoSlideActive = computed(
	() =>
		!!props.videoUrl &&
		(standardSliderRef.value?.currentCarouselItemIndex ?? 0) === 0
);
const shouldShowMobileVideoControls = computed(
	() =>
		isMobileViewport.value &&
		isVideoSlideActive.value &&
		!!mobileVideoPlayerRef.value &&
		!mobileVideoPlayerRef.value.hasError
);
const mobileVideoProgress = computed(
	() => mobileVideoPlayerRef.value?.progress ?? 0
);
const mobileVideoPlaying = computed(
	() => mobileVideoPlayerRef.value?.playing ?? false
);
const mobileVideoHasStartedPlaying = computed(
	() => mobileVideoPlayerRef.value?.hasStartedPlaying ?? false
);
const mobileVideoMuted = computed(
	() => mobileVideoPlayerRef.value?.isMuted ?? true
);

function getImageAlt(index: number | null) {
	return getPropertyImageAlt(
		props.property?.propertyType?.name,
		props.property?.suites,
		props.property?.region,
		props.property?.city,
		props.property?.askingPrice,
		index
	);
}

function handlePhotoClick(
	photoIndex: number,
	photoUrl: string,
	clickType: 'photo' | 'cta' = 'photo'
) {
	emit('photo-clicked', {
		photoIndex,
		photoUrl,
		clickType
	});
}

function handleVideoClick(videoUrl?: string) {
	emit('video-clicked', videoUrl);
}

function handleMobileVideoTogglePlay() {
	void mobileVideoPlayerRef.value?.togglePlay();
}

function handleMobileVideoToggleMute() {
	mobileVideoPlayerRef.value?.toggleMute();
}

function handleMobileVideoSeek(ratio: number) {
	mobileVideoPlayerRef.value?.seekTo(ratio);
}

const mobileCarouselCountBus = useEventBus(
	EventBusKeys.mobileHeroSectionPhotoCount
);

watch(
	currentPhotoCount,
	newValue => {
		mobileCarouselCountBus.emit(newValue);
	},
	{ immediate: true }
);

onBeforeUnmount(() => {
	mobileVideoPlayerRef.value?.pause();
});
</script>

<template>
	<div class="relative size-full bg-black xl:hidden">
		<component
			:is="StandardSlider<CarouselItem>"
			ref="standardSliderRef"
			:items="carouselItems"
			:is-loading="isLoading"
			:section-name="sectionName"
			carousel-item-size="half-seamless"
			:slides-to-scroll="1"
			:carousel-options="{
				cyclic: false,
				align: 'center',
				class: 'size-full !bg-black',
				carouselWrapperClasses: 'h-full !bg-black'
			}"
			:show-navigation-arrows="true"
			:always-show-arrows="true"
			:display-title="false"
			:display-footer="false"
			:limit-photos="false"
			carousel-item-class="!bg-black"
			title=""
			container-class="!p-0 h-full !bg-black"
			main-container-class="h-full !bg-black"
			class="h-full"
			carousel-content-class="!p-0 h-full !gap-1 !bg-black"
		>
			<template #items="{ item, index }">
				<div class="h-full w-full overflow-hidden bg-black">
					<ClientOnly v-if="item.type === 'video' && item.videoUrl">
						<HlsVideoPlayer
							ref="mobileVideoPlayerRef"
							:video-url="item.videoUrl"
							:poster-url="item.posterUrl"
							:autoplay="true"
							:muted="true"
							:controls="!isMobileViewport"
							:show-progress-bar="!isMobileViewport"
							:show-play-pause-control="!isMobileViewport"
							:loop="true"
							interaction-mode="emit-click"
							class="size-full"
							@click="handleVideoClick(item.videoUrl)"
						/>
						<template #fallback>
							<!-- Show poster thumbnail while component hydrates -->
							<div class="flex size-full items-center justify-center bg-black">
								<NuxtImg
									v-if="item.posterUrl"
									:src="item.posterUrl"
									alt="Video thumbnail"
									class="size-full object-cover border-0 outline-none"
									format="webp"
									quality="80"
									loading="eager"
									fetchpriority="high"
								/>
							</div>
						</template>
					</ClientOnly>

					<Skeleton
						v-if="item.type === 'image' && item.image"
						:is-active="isLoading"
						class="size-full"
						variant="black"
					>
						<NuxtImg
							:src="item.image!.watermarkUrl"
							:alt="getImageAlt(index)"
							width="640"
							height="360"
							sizes="xs:375px sm:640px md:512px lg:512px"
							densities="x1"
							:preload="index === 0 ? { fetchPriority: 'high' } : undefined"
							:loading="index === 0 ? 'eager' : 'lazy'"
							decoding="async"
							class="aspect-video size-full object-cover"
							format="webp"
							quality="80"
							@click="
								handlePhotoClick(item.index ?? 0, item.image.watermarkUrl)
							"
						/>
					</Skeleton>
				</div>
			</template>

			<template #skeleton>
				<div class="size-full">
					<Skeleton class="size-full" variant="black" :is-active="true" />
				</div>
			</template>
		</component>

		<div
			v-if="shouldShowMobileVideoControls"
			class="absolute inset-x-4 bottom-[68px] z-30 flex items-center gap-3 md:inset-x-5"
			@click.stop
		>
			<div class="flex shrink-0 items-center gap-2">
				<GlassButton
					v-if="mobileVideoHasStartedPlaying"
					shape="circle"
					class="size-8 shrink-0 text-white"
					:aria-label="mobileVideoPlaying ? 'Pausar vídeo' : 'Reproduzir vídeo'"
					@click="handleMobileVideoTogglePlay"
				>
					<template v-if="mobileVideoPlaying">
						<span class="sr-only">Pausar vídeo</span>
						<span aria-hidden="true" class="flex items-center gap-0.5">
							<span class="h-3 w-[3px] rounded-full bg-white" />
							<span class="h-3 w-[3px] rounded-full bg-white" />
						</span>
					</template>

					<IconMaterialSymbolsLightPlayArrow
						v-else
						class="size-4 translate-x-[0.5px]"
					/>
				</GlassButton>

				<GlassButton
					shape="circle"
					class="size-8 shrink-0 text-white"
					:aria-label="mobileVideoMuted ? 'Ativar som' : 'Desativar som'"
					@click="handleMobileVideoToggleMute"
				>
					<IconMaterialSymbolsLightVolumeOff
						v-if="mobileVideoMuted"
						class="size-4"
					/>
					<IconMaterialSymbolsLightVolumeUp v-else class="size-4" />
				</GlassButton>
			</div>

			<div class="min-w-0 flex-1">
				<VideoProgressBar
					:progress="mobileVideoProgress"
					@seek="handleMobileVideoSeek"
				/>
			</div>
		</div>
	</div>
</template>
